<template>
  <v-container class="modern-faq-container" fluid ma-0 pa-0>
    <!-- Modern Header -->
    <div class="modern-faq-header">
      <div class="header-background">
        <div class="header-circle header-circle-1"></div>
        <div class="header-circle header-circle-2"></div>
        <div class="header-circle header-circle-3"></div>
      </div>
      <div class="header-content-wrapper">
        <div class="header-icon-wrapper">
          <v-icon size="56" color="white">mdi-help-circle</v-icon>
        </div>
        <div class="header-text-content">
          <h1 class="modern-faq-title">Frequently Asked Questions</h1>
          <p class="modern-faq-subtitle">
            <v-icon small color="white" class="mr-1">mdi-information</v-icon>
            Find answers to common questions about our organization
          </p>
        </div>
      </div>
    </div>

    <v-row class="ma-0 px-4 py-6" justify="center">
      <v-col cols="12" md="10" lg="8">
        <!-- FAQ Stats Cards -->
        <v-row class="mb-6">
          <v-col cols="12" sm="4" class="pa-2">
            <v-card class="modern-stats-card membership-card" elevation="0">
              <div class="stats-card-content">
                <div class="stats-icon-wrapper membership-icon">
                  <v-icon color="white" size="28">mdi-account-group</v-icon>
                </div>
                <div class="stats-info">
                  <div class="stats-value">{{ membershipCount }}</div>
                  <div class="stats-label">Membership FAQs</div>
                </div>
              </div>
            </v-card>
          </v-col>
          <v-col cols="12" sm="4" class="pa-2">
            <v-card class="modern-stats-card payment-card" elevation="0">
              <div class="stats-card-content">
                <div class="stats-icon-wrapper payment-icon">
                  <v-icon color="white" size="28">mdi-cash-multiple</v-icon>
                </div>
                <div class="stats-info">
                  <div class="stats-value">{{ paymentCount }}</div>
                  <div class="stats-label">Payment FAQs</div>
                </div>
              </div>
            </v-card>
          </v-col>
          <v-col cols="12" sm="4" class="pa-2">
            <v-card class="modern-stats-card tech-card" elevation="0">
              <div class="stats-card-content">
                <div class="stats-icon-wrapper tech-icon">
                  <v-icon color="white" size="28">mdi-laptop</v-icon>
                </div>
                <div class="stats-info">
                  <div class="stats-value">{{ techCount }}</div>
                  <div class="stats-label">Tech FAQs</div>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!-- FAQ Accordion Section -->
        <div class="modern-faq-section mb-6">
          <div class="faq-section-header">
            <div class="section-icon-badge">
              <v-icon color="white" size="24">mdi-frequently-asked-questions</v-icon>
            </div>
            <h2 class="faq-section-title">Common Questions</h2>
          </div>

          <v-card class="modern-faq-card" elevation="0">
            <v-expansion-panels class="modern-expansion-panels" accordion>
              <v-expansion-panel
                v-for="(item, index) in myItems"
                :key="index"
                class="modern-expansion-panel"
              >
                <v-expansion-panel-header class="modern-panel-header">
                  <div class="panel-header-content">
                    <div class="panel-icon-wrapper" :class="getCategoryClass(item.category)">
                      <v-icon color="white" small>{{ getCategoryIcon(item.category) }}</v-icon>
                    </div>
                    <div class="panel-title-wrapper">
                      <span class="panel-title">{{ item.title }}</span>
                      <v-chip x-small class="panel-category-chip" :class="getCategoryChipClass(item.category)">
                        {{ item.category }}
                      </v-chip>
                    </div>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content class="modern-panel-content">
                  <div class="panel-answer" v-html="item.value"></div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-card>
        </div>

        <!-- Contact Form Section -->
        <div class="modern-contact-section">
          <div class="contact-section-header">
            <div class="section-icon-badge contact-badge">
              <v-icon color="white" size="24">mdi-email</v-icon>
            </div>
            <h2 class="contact-section-title">Still Have Questions?</h2>
            <p class="contact-section-subtitle">Send us a message and we'll get back to you</p>
          </div>

          <v-card class="modern-contact-card" elevation="0">
            <v-card-text class="pa-6">
              <v-form ref="form" v-model="valid" lazy-validation>
                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      ref="Json.FirstName"
                      v-model="Json.FirstName"
                      :rules="[rules.required]"
                      label="First Name"
                      placeholder="Enter your first name"
                      outlined
                      dense
                      class="modern-input"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon color="primary" size="20">mdi-account</v-icon>
                      </template>
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      ref="Json.LastName"
                      v-model="Json.LastName"
                      :rules="[rules.required]"
                      label="Last Name"
                      placeholder="Enter your last name"
                      outlined
                      dense
                      class="modern-input"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon color="primary" size="20">mdi-account</v-icon>
                      </template>
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      ref="Json.Email"
                      v-model="Json.Email"
                      :rules="emailRules"
                      label="Email Address"
                      placeholder="your.email@example.com"
                      type="email"
                      outlined
                      dense
                      class="modern-input"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon color="primary" size="20">mdi-email</v-icon>
                      </template>
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      ref="Json.Mobile"
                      v-model="Json.Mobile"
                      :rules="[rules.required]"
                      label="Mobile Number"
                      placeholder="(123) 456-7890"
                      outlined
                      dense
                      class="modern-input"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon color="primary" size="20">mdi-phone</v-icon>
                      </template>
                    </v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <v-textarea
                      ref="Json.Comments"
                      v-model="Json.Comments"
                      :rules="[rules.required]"
                      label="Your Message"
                      placeholder="Tell us what you need help with..."
                      outlined
                      rows="4"
                      counter
                      class="modern-input"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon color="primary" size="20">mdi-message-text</v-icon>
                      </template>
                    </v-textarea>
                  </v-col>
                </v-row>

                <v-card-actions class="px-0 pt-4">
                  <v-spacer></v-spacer>
                  <v-btn
                    class="modern-btn clear-btn"
                    elevation="0"
                    @click="$refs.form.reset()"
                  >
                    <v-icon left small>mdi-close</v-icon>
                    Clear
                  </v-btn>
                  <v-btn
                    :disabled="!valid"
                    :loading="isLoading"
                    class="modern-btn submit-btn"
                    elevation="0"
                    @click="submit"
                  >
                    <v-icon left small>mdi-send</v-icon>
                    Submit
                  </v-btn>
                </v-card-actions>
              </v-form>
            </v-card-text>
          </v-card>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>
  

  <script>
import ReportServices from '@/services/ReportServices'
//import VueFaqAccordion from "vue-faq-accordion";

  export default {
    provide: {
      heading: { align: 'center' },
    },
    data: () => ({
      valid: true,
      email: undefined,
      form: false,
      isLoading: false,
      password: undefined,
      phone: undefined,
      Json: {
        _id: '',
        FirstName: '',
        LastName: '',
        Email: '',
        Mobile: '',
        Comments: ''
      },
      emailRules: [
        v => !!v || 'E-mail is required',
        v => /.+@.+\..+/.test(v) || 'E-mail must be valid',
      ],
      rules: {
        length: len => v => (v || '').length >= len || `Invalid character length, required ${len}`,
        password: v => (v || '').match(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*(_|[^\w])).+$/) || 'Password must contain an upper case letter, a numeric character, and a special character',
        required: v => !!v || 'This field is required',
      },
      myItems: [
          {
            title: "How long my Membership is valid ?",
            value: "Membership is valid for respective calendar year",
            category: "Membership"
          },
          {
            title: "Who to reach out to for Membership related questions",
            value: "Contact the team at contact@cltts.org",
            category: "Membership"
          },
          {
            title: "Who to reach out to for payment and billing related questions? ",
            value: "Reach out to treasurer, Rakesh @  201.300.9316",
            category: "Payment"
          },
          {
            category: "Tech",
            title: "Portal Login/ Registration/ Website related issues",
            value: "Please reach out to Narasimman - tech@cltts.org"
          },
          {
            title: "Where is tamil Sangam located ?",
            value: "சார்லட் தமிழ்ச் சங்கம் <br><br> Email  : contact@cltts.org<br>Address : P.O BOX 78131 CHARLOTTE, <br> NORTH CAROLINA 28271",
            category: "Membership"
          },
        ]
    }),
    components: {
      
    },
    computed: {
      membershipCount() {
        return this.myItems.filter(item => item.category === 'Membership').length;
      },
      paymentCount() {
        return this.myItems.filter(item => item.category === 'Payment').length;
      },
      techCount() {
        return this.myItems.filter(item => item.category === 'Tech').length;
      }
    },
  methods: {
    getCategoryIcon(category) {
      const icons = {
        'Membership': 'mdi-account-group',
        'Payment': 'mdi-cash-multiple',
        'Tech': 'mdi-laptop'
      };
      return icons[category] || 'mdi-help-circle';
    },
    getCategoryClass(category) {
      const classes = {
        'Membership': 'membership-icon-small',
        'Payment': 'payment-icon-small',
        'Tech': 'tech-icon-small'
      };
      return classes[category] || 'default-icon-small';
    },
    getCategoryChipClass(category) {
      const classes = {
        'Membership': 'membership-chip',
        'Payment': 'payment-chip',
        'Tech': 'tech-chip'
      };
      return classes[category] || 'default-chip';
    },
    async cleardata () {
        this.Json.FirstName = ''
        this.Json.LastName = ''
        this.Json.Email = ''
        this.Json.Mobile = ''
        this.Json.Comments = ''
    },
    async submit () {
      if (this.$refs.form.validate()) {
        ReportServices.submitToContactUs(this.Json)
        setTimeout(() => {
          this.$router.push('/ThankYou')
        }, 500)
      }
    }
  },
  async mounted () {
    this.loading = false
  }
}
</script>


<style scoped lang="scss">
* {
  font-family: "Avenir", Helvetica, Arial, sans-serif;
}

/* Container */
.modern-faq-container {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%) !important;
  min-height: 100vh !important;
}

/* Modern Header */
.modern-faq-header {
  position: relative !important;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  padding: 50px 24px !important;
  overflow: hidden !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15) !important;
}

.header-background {
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  overflow: hidden !important;
}

.header-circle {
  position: absolute !important;
  border-radius: 50% !important;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.15) 0%, transparent 70%) !important;
}

.header-circle-1 {
  width: 400px !important;
  height: 400px !important;
  top: -200px !important;
  right: -100px !important;
}

.header-circle-2 {
  width: 300px !important;
  height: 300px !important;
  bottom: -150px !important;
  left: -50px !important;
}

.header-circle-3 {
  width: 200px !important;
  height: 200px !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  opacity: 0.5 !important;
}

.header-content-wrapper {
  position: relative !important;
  z-index: 2 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 24px !important;
  flex-wrap: wrap !important;
}

.header-icon-wrapper {
  width: 80px !important;
  height: 80px !important;
  border-radius: 20px !important;
  background: rgba(255, 255, 255, 0.2) !important;
  backdrop-filter: blur(20px) !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  box-shadow:
    0 8px 24px rgba(0, 0, 0, 0.15),
    inset 0 2px 4px rgba(255, 255, 255, 0.3) !important;
}

.header-text-content {
  text-align: center !important;
}

.modern-faq-title {
  font-size: 42px !important;
  font-weight: 800 !important;
  color: white !important;
  margin: 0 !important;
  text-shadow: 0 4px 12px rgba(0, 0, 0, 0.2) !important;
  letter-spacing: -0.5px !important;
}

.modern-faq-subtitle {
  font-size: 16px !important;
  color: rgba(255, 255, 255, 0.95) !important;
  margin: 8px 0 0 0 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 4px !important;
}

/* Stats Cards */
.modern-stats-card {
  border-radius: 16px !important;
  background: linear-gradient(145deg,
    rgba(255, 255, 255, 0.95) 0%,
    rgba(248, 250, 252, 0.9) 100%) !important;
  backdrop-filter: blur(30px) !important;
  border: 2px solid rgba(255, 255, 255, 0.8) !important;
  box-shadow:
    0 12px 32px rgba(0, 0, 0, 0.12),
    0 4px 12px rgba(0, 0, 0, 0.08),
    inset 0 2px 4px rgba(255, 255, 255, 1) !important;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
  position: relative !important;
  overflow: hidden !important;
}

.modern-stats-card::before {
  content: '' !important;
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  height: 4px !important;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 50%, #f093fb 100%) !important;
  background-size: 200% 100% !important;
  animation: gradientFlow 3s ease infinite !important;
}

.membership-card::before {
  background: linear-gradient(90deg, #667eea 0%, #764ba2 50%, #f093fb 100%) !important;
}

.payment-card::before {
  background: linear-gradient(90deg, #4CAF50 0%, #388E3C 50%, #81C784 100%) !important;
}

.tech-card::before {
  background: linear-gradient(90deg, #FF9800 0%, #F57C00 50%, #FFB74D 100%) !important;
}

.modern-stats-card:hover {
  transform: translateY(-8px) scale(1.02) !important;
  box-shadow:
    0 20px 48px rgba(0, 0, 0, 0.18),
    0 8px 16px rgba(0, 0, 0, 0.12),
    inset 0 2px 4px rgba(255, 255, 255, 1) !important;
}

.stats-card-content {
  padding: 20px !important;
  display: flex !important;
  align-items: center !important;
  gap: 16px !important;
}

.stats-icon-wrapper {
  width: 56px !important;
  height: 56px !important;
  border-radius: 14px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  box-shadow:
    0 4px 12px rgba(0, 0, 0, 0.15),
    inset 0 1px 2px rgba(255, 255, 255, 0.3) !important;
  position: relative !important;
  overflow: hidden !important;
}

.stats-icon-wrapper::before {
  content: '' !important;
  position: absolute !important;
  top: -50% !important;
  left: -50% !important;
  width: 200% !important;
  height: 200% !important;
  background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.3), transparent) !important;
  transform: rotate(45deg) !important;
  animation: shimmer 3s infinite !important;
}

.membership-icon {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
}

.payment-icon {
  background: linear-gradient(135deg, #4CAF50 0%, #388E3C 100%) !important;
}

.tech-icon {
  background: linear-gradient(135deg, #FF9800 0%, #F57C00 100%) !important;
}

.stats-info {
  flex: 1 !important;
}

.stats-value {
  font-size: 32px !important;
  font-weight: 800 !important;
  color: #1f2937 !important;
  line-height: 1 !important;
  margin-bottom: 4px !important;
}

.stats-label {
  font-size: 13px !important;
  font-weight: 600 !important;
  color: #64748b !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
}

/* FAQ Section */
.modern-faq-section {
  margin-bottom: 32px !important;
}

.faq-section-header {
  display: flex !important;
  align-items: center !important;
  gap: 16px !important;
  margin-bottom: 24px !important;
  padding: 0 8px !important;
}

.section-icon-badge {
  width: 48px !important;
  height: 48px !important;
  border-radius: 12px !important;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  box-shadow:
    0 4px 12px rgba(102, 126, 234, 0.4),
    inset 0 1px 2px rgba(255, 255, 255, 0.3) !important;
}

.contact-badge {
  background: linear-gradient(135deg, #4CAF50 0%, #388E3C 100%) !important;
  box-shadow:
    0 4px 12px rgba(76, 175, 80, 0.4),
    inset 0 1px 2px rgba(255, 255, 255, 0.3) !important;
}

.faq-section-title {
  font-size: 28px !important;
  font-weight: 700 !important;
  color: #1f2937 !important;
  margin: 0 !important;
}

.modern-faq-card {
  border-radius: 16px !important;
  background: transparent !important;
  overflow: visible !important;
}

/* Expansion Panels */
.modern-expansion-panels {
  background: transparent !important;
  gap: 12px !important;
  display: flex !important;
  flex-direction: column !important;
}

.modern-expansion-panel {
  border-radius: 14px !important;
  background: linear-gradient(145deg,
    rgba(255, 255, 255, 0.95) 0%,
    rgba(248, 250, 252, 0.9) 100%) !important;
  backdrop-filter: blur(30px) !important;
  border: 2px solid rgba(255, 255, 255, 0.8) !important;
  box-shadow:
    0 4px 16px rgba(0, 0, 0, 0.08),
    inset 0 1px 2px rgba(255, 255, 255, 1) !important;
  margin-bottom: 12px !important;
  overflow: hidden !important;
  transition: all 0.3s ease !important;
}

.modern-expansion-panel:hover {
  transform: translateX(4px) !important;
  box-shadow:
    0 8px 24px rgba(0, 0, 0, 0.12),
    inset 0 1px 2px rgba(255, 255, 255, 1) !important;
}

.modern-expansion-panel::before {
  display: none !important;
}

.modern-panel-header {
  padding: 20px 24px !important;
  min-height: auto !important;
}

.panel-header-content {
  display: flex !important;
  align-items: center !important;
  gap: 16px !important;
  width: 100% !important;
}

.panel-icon-wrapper {
  width: 40px !important;
  height: 40px !important;
  border-radius: 10px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  flex-shrink: 0 !important;
  box-shadow:
    0 2px 8px rgba(0, 0, 0, 0.15),
    inset 0 1px 2px rgba(255, 255, 255, 0.3) !important;
}

.membership-icon-small {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
}

.payment-icon-small {
  background: linear-gradient(135deg, #4CAF50 0%, #388E3C 100%) !important;
}

.tech-icon-small {
  background: linear-gradient(135deg, #FF9800 0%, #F57C00 100%) !important;
}

.default-icon-small {
  background: linear-gradient(135deg, #64748b 0%, #475569 100%) !important;
}

.panel-title-wrapper {
  flex: 1 !important;
  display: flex !important;
  flex-direction: column !important;
  gap: 8px !important;
}

.panel-title {
  font-size: 16px !important;
  font-weight: 600 !important;
  color: #1f2937 !important;
  line-height: 1.4 !important;
}

.panel-category-chip {
  align-self: flex-start !important;
  font-weight: 600 !important;
  font-size: 11px !important;
  height: 22px !important;
  padding: 0 10px !important;
  color: white !important;
}

.membership-chip {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
}

.payment-chip {
  background: linear-gradient(135deg, #4CAF50 0%, #388E3C 100%) !important;
}

.tech-chip {
  background: linear-gradient(135deg, #FF9800 0%, #F57C00 100%) !important;
}

.default-chip {
  background: linear-gradient(135deg, #64748b 0%, #475569 100%) !important;
}

.modern-panel-content {
  padding: 0 24px 24px 80px !important;
}

.panel-answer {
  font-size: 15px !important;
  color: #4b5563 !important;
  line-height: 1.7 !important;
  padding: 16px !important;
  background: rgba(102, 126, 234, 0.05) !important;
  border-radius: 10px !important;
  border-left: 4px solid #667eea !important;
}

/* Contact Section */
.modern-contact-section {
  margin-top: 32px !important;
}

.contact-section-header {
  display: flex !important;
  flex-direction: column !important;
  align-items: center !important;
  gap: 12px !important;
  margin-bottom: 24px !important;
  text-align: center !important;
}

.contact-section-title {
  font-size: 28px !important;
  font-weight: 700 !important;
  color: #1f2937 !important;
  margin: 0 !important;
}

.contact-section-subtitle {
  font-size: 15px !important;
  color: #64748b !important;
  margin: 0 !important;
}

.modern-contact-card {
  border-radius: 16px !important;
  background: linear-gradient(145deg,
    rgba(255, 255, 255, 0.95) 0%,
    rgba(248, 250, 252, 0.9) 100%) !important;
  backdrop-filter: blur(30px) !important;
  border: 2px solid rgba(255, 255, 255, 0.8) !important;
  box-shadow:
    0 12px 32px rgba(0, 0, 0, 0.12),
    0 4px 12px rgba(0, 0, 0, 0.08),
    inset 0 2px 4px rgba(255, 255, 255, 1) !important;
}

/* Modern Input Fields */
.modern-input::v-deep .v-input__control {
  border-radius: 12px !important;
}

.modern-input::v-deep .v-input__slot {
  background: rgba(255, 255, 255, 0.8) !important;
  border-radius: 12px !important;
  border: 2px solid rgba(102, 126, 234, 0.2) !important;
  transition: all 0.3s ease !important;
}

.modern-input::v-deep .v-input__slot:hover {
  border-color: rgba(102, 126, 234, 0.4) !important;
  background: rgba(255, 255, 255, 1) !important;
}

.modern-input::v-deep .v-input--is-focused .v-input__slot {
  border-color: #667eea !important;
  background: rgba(255, 255, 255, 1) !important;
  box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1) !important;
}

.modern-input::v-deep .v-label {
  font-weight: 600 !important;
  color: #64748b !important;
  font-size: 14px !important;
}

.modern-input::v-deep .v-text-field__slot input,
.modern-input::v-deep .v-text-field__slot textarea {
  color: #1f2937 !important;
  font-size: 14px !important;
}

.modern-input::v-deep .v-input__prepend-inner {
  margin-top: 12px !important;
  margin-right: 8px !important;
}

/* Modern Buttons */
.modern-btn {
  border-radius: 10px !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  text-transform: none !important;
  letter-spacing: 0.3px !important;
  padding: 10px 24px !important;
  transition: all 0.3s ease !important;
}

.clear-btn {
  background: linear-gradient(135deg, #64748b 0%, #475569 100%) !important;
  color: white !important;
  box-shadow:
    0 4px 12px rgba(100, 116, 139, 0.3),
    inset 0 1px 2px rgba(255, 255, 255, 0.2) !important;
}

.clear-btn:hover {
  transform: translateY(-2px) !important;
  box-shadow:
    0 6px 16px rgba(100, 116, 139, 0.4),
    inset 0 1px 2px rgba(255, 255, 255, 0.2) !important;
}

.submit-btn {
  background: linear-gradient(135deg, #4CAF50 0%, #388E3C 100%) !important;
  color: white !important;
  box-shadow:
    0 4px 12px rgba(76, 175, 80, 0.3),
    inset 0 1px 2px rgba(255, 255, 255, 0.2) !important;
}

.submit-btn:hover {
  transform: translateY(-2px) !important;
  box-shadow:
    0 6px 16px rgba(76, 175, 80, 0.4),
    inset 0 1px 2px rgba(255, 255, 255, 0.2) !important;
}

.submit-btn:disabled {
  background: rgba(100, 116, 139, 0.3) !important;
  color: rgba(255, 255, 255, 0.5) !important;
  box-shadow: none !important;
}

/* Animations */
@keyframes gradientFlow {
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%) translateY(-100%) rotate(45deg);
  }
  100% {
    transform: translateX(100%) translateY(100%) rotate(45deg);
  }
}

/* Responsive Design */
@media (max-width: 960px) {
  .modern-faq-title {
    font-size: 36px !important;
  }

  .modern-faq-subtitle {
    font-size: 14px !important;
  }

  .faq-section-title,
  .contact-section-title {
    font-size: 24px !important;
  }

  .modern-panel-content {
    padding: 0 24px 24px 24px !important;
  }
}

@media (max-width: 600px) {
  .modern-faq-header {
    padding: 40px 16px !important;
  }

  .header-icon-wrapper {
    width: 64px !important;
    height: 64px !important;
  }

  .modern-faq-title {
    font-size: 28px !important;
  }

  .modern-faq-subtitle {
    font-size: 13px !important;
  }

  .stats-value {
    font-size: 28px !important;
  }

  .stats-label {
    font-size: 11px !important;
  }

  .faq-section-title,
  .contact-section-title {
    font-size: 20px !important;
  }

  .section-icon-badge {
    width: 40px !important;
    height: 40px !important;
  }

  .modern-panel-header {
    padding: 16px !important;
  }

  .panel-icon-wrapper {
    width: 36px !important;
    height: 36px !important;
  }

  .panel-title {
    font-size: 14px !important;
  }

  .modern-panel-content {
    padding: 0 16px 16px 16px !important;
  }

  .panel-answer {
    font-size: 14px !important;
    padding: 12px !important;
  }

  .modern-btn {
    padding: 8px 16px !important;
    font-size: 13px !important;
  }
}
</style>
